/**
 * Environment utility functions
 * Provides helper functions for determining environment state and development mode
 */

/**
 * Check if the application is running in development mode
 * @returns boolean indicating if in development mode
 */
export const isDevEnvironment = (): boolean => {
  return process.env.NODE_ENV === 'development';
};

/**
 * Check if a specific feature should use development mode
 * This allows for more granular control over which features use dev mode
 * @param feature Optional feature name to check
 * @returns boolean indicating if the feature should use dev mode
 */
export const useDevMode = (feature?: string): boolean => {
  // Always return true for development environment
  if (process.env.NODE_ENV === 'development') {
    return true;
  }
  
  // For production, check if dev mode is explicitly enabled
  const devMode = localStorage.getItem('devMode');
  if (!devMode) return false;
  
  try {
    const devModeSettings = JSON.parse(devMode);
    
    // If feature is specified, check if that specific feature is enabled
    if (feature && devModeSettings.features) {
      return devModeSettings.active && devModeSettings.features[feature];
    }
    
    // Otherwise just check if dev mode is active
    return devModeSettings.active;
  } catch (e) {
    return false;
  }
};

/**
 * Get the current user role in development mode
 * @returns string representing the user role in dev mode, or null if not in dev mode
 */
export const getDevModeRole = (): string | null => {
  if (!useDevMode()) return null;
  
  const devMode = localStorage.getItem('devMode');
  if (!devMode) return null;
  
  try {
    const devModeSettings = JSON.parse(devMode);
    return devModeSettings.role || 'owner'; // Default to owner for dev mode
  } catch (e) {
    return null;
  }
};

/**
 * Enable development mode for the application
 * @param role Optional role to use in development mode (defaults to owner)
 * @param features Optional object with feature flags
 */
export const enableDevMode = (role: string = 'owner', features: Record<string, boolean> = {}): void => {
  const devModeSettings = {
    active: true,
    role,
    features: {
      staffManagement: true, // Enable staff management by default
      ...features
    }
  };
  
  localStorage.setItem('devMode', JSON.stringify(devModeSettings));
};

/**
 * Disable development mode for the application
 */
export const disableDevMode = (): void => {
  localStorage.removeItem('devMode');
};
